import { type ReactNode } from "react";
import { type MessageProps } from "../Messages/Message";

import {
  Alert,
  type ButtonProps,
  Button,
  styled,
  Typography,
} from "~/common/mui";
import { Link, type LinkProps } from "~/common/components/Link";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { useTranslation } from "react-i18next";

const CenterContentsRoot = styled("div", {
  shouldForwardProp: (prop) => prop !== "leftAlign",
})<{ leftAlign?: boolean }>(({ theme, leftAlign }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  marginTop: 32,
  ...(leftAlign && {
    textAlign: "left",
    alignItems: "flex-start",
  }),
  "& > * + *": {
    marginTop: 16,
    [theme.breakpoints.up("desktop-s")]: {
      marginTop: 20,
    },
  },
  "& > img + *": {
    marginTop: 32,
  },
  "& > * + img": {
    marginTop: 32,
  },
}));

const HeadlineTypography = styled(Typography)(({ theme }) => ({
  margin: 0,
  ...theme.typography.headlineSmall,
  [theme.breakpoints.up("desktop-s")]: {
    ...theme.typography.headlineMedium,
  },
}));

const SubheadlineTypography = styled(Typography)(({ theme }) => ({
  margin: 0,
  ...theme.typography.labelLarge,
  [theme.breakpoints.up("desktop-s")]: {
    ...theme.typography.titleMedium,
  },
}));

const StyledImage = styled("img")({ margin: 0 });

const GoBackLink = styled(Link)({
  marginTop: 24,
});

const StyledCtaButton = styled(Button)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("tablet")]: {
    width: "auto",
  },
}));

export const InformationPage = (props: InformationPageProps) => (
  <CenterContents>
    {props.title}
    {!!props.subtitle && props.subtitle}

    <Image src={props.imageSrc} />
    {props.btnSetup && (
      <Button onClick={props.btnSetup.onClick} href={props.btnSetup.href} id={props.btnSetup.id}>
        {props.btnSetup.label}
      </Button>
    )}
    {!!props.informationMsg && <InformationMessage>{props.informationMsg}</InformationMessage>}
  </CenterContents>
);

export interface InformationPageProps {
  title: ReactNode;
  subtitle?: ReactNode;
  btnSetup?: {
    onClick?: () => void;
    href?: string;
    label: ReactNode;
    id?: string;
  };
  imageSrc: string;
  informationMsg?: ReactNode;
}

const CenterContents = ({ className, leftAlign, ...rest }: JSX.IntrinsicElements["div"] & { leftAlign?: boolean }) => (
  <CenterContentsRoot leftAlign={leftAlign} className={className} {...rest} />
);
InformationPage.CenterContents = CenterContents;

const Image = ({ className, ...rest }: JSX.IntrinsicElements["img"]) => (
  <StyledImage className={className} {...rest} />
);
InformationPage.Image = Image;

const Title = ({ className, as = "h1", ...rest }: BaseHtmlAttributes & VariableHtmlTag) => (
  <HeadlineTypography component={as} className={className} {...rest} />
);
InformationPage.Title = Title;

const Subtitle = ({ className, as = "p", ...rest }: BaseHtmlAttributes & VariableHtmlTag) => (
  <SubheadlineTypography component={as} className={className} {...rest} />
);
InformationPage.Subtitle = Subtitle;

const InformationMessage = ({ type = "info", className, ...rest }: Partial<MessageProps>) => (
  <Alert severity={type} className={className} sx={{ mt: "60px" }} {...rest} />
);
InformationPage.InformationMessage = InformationMessage;

const GoHomepageLink = ({ className, ...rest }: Partial<LinkProps>) => {
  const { t } = useTranslation();
  return (
    <GoBackLink className={className} href={HOMEPAGE} {...rest}>
      {t("emptyComparison.backHome")}
    </GoBackLink>
  );
};
InformationPage.GoHomepageLink = GoHomepageLink;

const CtaButton = ({ className, ...rest }: Partial<ButtonProps>) => (
  <StyledCtaButton className={className} {...rest} />
);
InformationPage.CtaButton = CtaButton;
